// src/pages/Users/Inscription/dossier/hooks/useInscriptionDossierPage.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";

import { getDossierInscription } from "../../../../../services/dossierServices";

import { getGroupName } from "../../../../../services/groupServices";

import { getAllSegments } from "../../../../../services/segmentDossierServices";

import { getSettings } from "../../../../../services/settingsServices";

async function safeGetDossier(
  adherentId,
) {
  try {
    return await getDossierInscription(
      adherentId,
    );
  } catch (error) {
    const status =
      error?.response?.status ??
      error?.status;

    if (status === 404) {
      return null;
    }

    console.error(
      "Erreur chargement dossier :",
      error,
    );

    return null;
  }
}

export default function useInscriptionDossierPage(
  userId,
) {
  const [loading, setLoading] =
    useState(true);

  const [adherents, setAdherents] =
    useState([]);

  const [dossiers, setDossiers] =
    useState({});

  const [groupNames, setGroupNames] =
    useState({});

  const [segments, setSegments] =
    useState([]);

  const [settings, setSettings] =
    useState({});

  /* ---------------------------------------------------------------------- */
  /* Chargement principal                                                   */
  /* ---------------------------------------------------------------------- */

  const loadPage = useCallback(
    async ({
      silent = false,
    } = {}) => {
      if (!userId) return;

      if (!silent) {
        setLoading(true);
      }

      try {
        /*
         * Les données indépendantes sont
         * chargées en parallèle.
         */
        const [
          adherentsData,
          settingsData,
          segmentsData,
        ] = await Promise.all([
          getAdherentsByUser(
            userId,
          ),

          getSettings().catch(
            () => [],
          ),

          getAllSegments().catch(
            () => [],
          ),
        ]);

        const list =
          Array.isArray(
            adherentsData,
          )
            ? adherentsData
            : [];

        setAdherents(list);

        setSettings(
          Array.isArray(settingsData)
            ? settingsData[0] || {}
            : settingsData || {},
        );

        setSegments(
          Array.isArray(segmentsData)
            ? segmentsData
            : [],
        );

        /*
         * Dossiers + groupes :
         * un appel par adhérent.
         */
        const [
          dossiersResults,
          groupsResults,
        ] = await Promise.all([
          Promise.all(
            list.map((adherent) =>
              safeGetDossier(
                adherent.id,
              ),
            ),
          ),

          Promise.all(
            list.map((adherent) =>
              getGroupName(
                adherent.id,
              ).catch(() => []),
            ),
          ),
        ]);

        const nextDossiers = {};
        const nextGroups = {};

        list.forEach(
          (adherent, index) => {
            nextDossiers[
              adherent.id
            ] =
              dossiersResults[index];

            nextGroups[
              adherent.id
            ] = Array.isArray(
              groupsResults[index],
            )
              ? groupsResults[index]
              : [];
          },
        );

        setDossiers(
          nextDossiers,
        );

        setGroupNames(
          nextGroups,
        );
      } catch (error) {
        console.error(
          "Erreur chargement page dossier :",
          error,
        );

        toast.error(
          "Erreur lors du chargement des dossiers.",
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [userId],
  );

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  /* ---------------------------------------------------------------------- */
  /* Rafraîchir uniquement un dossier                                       */
  /* ---------------------------------------------------------------------- */

  const refreshDossier =
    useCallback(
      async (adherentId) => {
        if (!adherentId) return;

        const dossier =
          await safeGetDossier(
            adherentId,
          );

        setDossiers(
          (previous) => ({
            ...previous,

            [adherentId]:
              dossier,
          }),
        );
      },
      [],
    );

  /* ---------------------------------------------------------------------- */

  const refreshAll =
    useCallback(async () => {
      await loadPage({
        silent: true,
      });
    }, [loadPage]);

  return {
    loading,

    adherents,
    dossiers,
    groupNames,

    segments,
    settings,

    activeSeason:
      settings?.saison_active || "",

    refreshDossier,
    refreshAll,
  };
}